import type { PixelEvent } from '@/events';

import type { QueuedEventRecord } from '../queue/storage/types';

export type OrderingConfig = {
  readonly enabled: boolean;
};

export type OrderingDebugState = {
  readonly enabled: boolean;
  readonly globalSequence: number;
  readonly sessionSequence: number;
  readonly lastCorrelationId: string | null;
  readonly recordCount: number;
};

export class OrderingManager {
  private readonly enabled: boolean;

  private globalSequence = 0;

  private sessionSequence = 0;

  private lastCorrelationId: string | null = null;

  constructor(config: OrderingConfig) {
    this.enabled = config.enabled;
  }

  assign(
    event: PixelEvent,
    correlationId?: string,
    transactionGroup?: string
  ): QueuedEventRecord {
    if (this.enabled) {
      this.globalSequence += 1;
      this.sessionSequence += 1;
    }

    if (correlationId) {
      this.lastCorrelationId = correlationId;
    }

    return {
      id: crypto.randomUUID(),
      event,
      sequence: this.globalSequence,
      sessionSequence: this.sessionSequence,
      correlationId: correlationId ?? this.lastCorrelationId ?? undefined,
      transactionGroup,
      enqueuedAt: Date.now(),
    };
  }

  sort(records: readonly QueuedEventRecord[]): QueuedEventRecord[] {
    if (!this.enabled) return [...records];

    return [...records].sort((left, right) => {
      if (left.sequence !== right.sequence) {
        return left.sequence - right.sequence;
      }
      return left.enqueuedAt - right.enqueuedAt;
    });
  }

  restoreFromRecords(records: readonly QueuedEventRecord[]): void {
    if (!this.enabled || records.length === 0) return;

    const maxGlobal = records.reduce(
      (max, record) => Math.max(max, record.sequence),
      0
    );
    const maxSession = records.reduce(
      (max, record) => Math.max(max, record.sessionSequence),
      0
    );

    this.globalSequence = Math.max(this.globalSequence, maxGlobal);
    this.sessionSequence = Math.max(this.sessionSequence, maxSession);

    const lastWithCorrelation = [...records]
      .reverse()
      .find(record => record.correlationId);
    if (lastWithCorrelation?.correlationId) {
      this.lastCorrelationId = lastWithCorrelation.correlationId;
    }
  }

  resetSessionSequence(): void {
    this.sessionSequence = 0;
  }

  getDebugState(recordCount = 0): OrderingDebugState {
    return {
      enabled: this.enabled,
      globalSequence: this.globalSequence,
      sessionSequence: this.sessionSequence,
      lastCorrelationId: this.lastCorrelationId,
      recordCount,
    };
  }
}
